import createDeepMerge from "@fastify/deepmerge";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type {
  LocalizationState,
  LocalizationStore,
  TranslationCollection,
} from "@/stores/localization-store/localization-store.types";
import type { SharedDataLocalization } from "@/types";

const initialLocalizationState: LocalizationState = {
  localizationLoading: true,
  translationVersion: 0,
  locale: null,
  language: null,
  fallbackLanguage: null,
  translations: [],
  fallbackTranslations: [],
};

const deepMerge = createDeepMerge({ all: true });

const getTranslationVersion = async (): Promise<number> => {
  const { default: data } = await import("@/lang/translation-version.json");

  return data.version;
};

const getTranslationFileContent = async (language: string) => {
  const { default: translations } = await import(`@/lang/${language}.json`);
  return translations;
};

export const useLocalizationStore = create<LocalizationStore>()(
  persist(
    (set, get) => ({
      ...initialLocalizationState,
      setLocalizationLoading: (localizationLoading: boolean) =>
        set({ localizationLoading }),
      setTranslationVersion: (translationVersion: number) =>
        set({ translationVersion }),
      setLocale: (locale: string) => set({ locale }),
      setLanguage: (language: string) => set({ language }),
      setFallbackLanguage: (fallbackLanguage: string) =>
        set({ fallbackLanguage }),
      setTranslations: (translations: TranslationCollection) =>
        set({ translations }),
      setFallbackTranslations: (fallbackTranslations: TranslationCollection) =>
        set({ fallbackTranslations }),

      storeLocalizationValues: async (
        localizationValues: SharedDataLocalization
      ) => {
        const state = get();
        state.setLocalizationLoading(true);

        const translationVersion = await getTranslationVersion();

        const originalLocale = state.locale;

        const { locale, language, fallbackLanguage } = localizationValues;

        state.setLocale(locale);
        state.setLanguage(language);
        state.setFallbackLanguage(fallbackLanguage);

        if (
          locale !== originalLocale ||
          Object.keys(state.translations).length <= 0 ||
          translationVersion !== state.translationVersion
        ) {
          await state.updateTranslations(language, fallbackLanguage);
        }

        state.setTranslationVersion(translationVersion);

        state.setLocalizationLoading(false);
      },

      updateTranslations: async (
        language: string,
        fallbackLanguage: string
      ) => {
        const translations = await getTranslationFileContent(language);

        get().setTranslations(translations);

        let fallbackTranslations = translations;

        if (language !== fallbackLanguage) {
          fallbackTranslations =
            await getTranslationFileContent(fallbackLanguage);
        }

        get().setFallbackTranslations(fallbackTranslations);
      },
    }),
    {
      name: "localization-store-",
      storage: createJSONStorage(() => sessionStorage),
      merge: (persisted, current) => deepMerge(current, persisted) as never,
    }
  )
);
